import "../soundslice";
import {
  subscribeToReceiveMessageFromSoundsliceIframeEvents
} from "../eventBus/soundslice/receiveMessageFromSoundsliceIframeEventBus";
import {SoundsliceResponseEventTypes} from "../eventBus/soundslice/SoundsliceResponseEventTypes";
import {subscribeToSegmentEvents} from "../eventBus/segment/segmentEventBus";
import {
  soundSliceSdk_clearLoop,
  soundSliceSdk_getDurationAsync,
  soundSliceSdk_pause,
  soundSliceSdk_play,
  soundSliceSdk_seek,
  soundSliceSdk_setHighlightCurrentBar,
  soundSliceSdk_setLoop,
  soundSliceSdk_setNotationLayout,
  soundSliceSdk_setPagesPerScreen,
  soundSliceSdk_setPlayheadType,
  soundSliceSdk_setProportionalNotation,
  soundSliceSdk_setProportionalSize,
  soundSliceSdk_setSpeed,
  soundSliceSdk_setTrackVisibility,
  soundSliceSdk_setVolume,
  soundSliceSdk_setZoom,
} from "../eventBus/soundslice/soundSliceSdk";
import Note from "../../models/soundslice/Note";
import Synthesizer from "../synthesizer/Synthesizer";
import NotesPlaybackScheduler from "./NotesPlaybackScheduler";
import metronome from "../metronome/metronome";
import countIn from "../countIn/countIn";
import {SegmentEventTypes} from "../eventBus/segment/SegmentEventTypes";
import Segment from "../../models/view/lessonPage/Segment";
import notesEventWorker from "./notesEventWorker";
import {
  clearAllNoteFeedback,
  emitDurationAndSeekOffsetSeconds_changed,
  emitNotesCurrentlySelected_changed,
  emitPlayback_ended,
  emitPlayback_started,
  emitProgress_changed,
  subscribeToNotesEvents,
} from "../eventBus/notes/notesEventBus";
import DemoMode from "../../models/view/lessonPage/DemoMode";
import ISpeedSelectOption from "../../models/view/lessonPage/ISpeedSelectOption";
import {SegmentModeEnum} from "../../models/view/lessonPage/SegmentModeEnum";
import {
  ISoundSliceRequestContext,
  SoundsliceTrackVisibilityTypes
} from "../eventBus/soundslice/SoundsliceRequestEventTypes";
import {IPlaybackStarted, NotesEventTypes} from "../eventBus/notes/NotesEventTypes";
import soundSlice from "../soundSlice/soundSlice";
import {subscribeToSettingsEvents} from "../eventBus/settings/settingsEventBus";
import {SettingsEventTypes} from "../eventBus/settings/SettingsEventTypes";
import AudioPlayer from "../audio/AudioPlayerSoundTouch";
import {subscribeToReceiveMessagesFromNativeApp} from "../eventBus/nativeApp/receiveMessageFromNativeAppEventBus";
import {FromNativeAppEventTypes} from "../eventBus/nativeApp/FromNativeAppEventTypes";
import {logger} from "../log/logger";
import nativeBridge from "../nativeBridge";
import deviceInfo from "../deviceInfo/deviceInfo";
import {play,} from "../../redux/lesson/lessonActions";
import {INote} from "../eventBus/nativeApp/ToNativeAppEventTypes";
import {getAudioContext} from "../audioContext";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import {NotationLayoutEnum} from "../../models/view/lessonPage/NotationLayoutEnum";
import lessonService from "../lesson/lessonService";
import {getNotationLayoutToApplyFromSelectedNotationLayout} from "../lesson/lessonUtility";
import {settingsStore} from "../../redux/settings/settingsStore";
import {arrayRemove, arrayUpsert} from "../../util/array-util";
import {IUserPreferencesForControlBarV2} from "../../models/view/lessonPage/IUserPreferencesForControlBar";
import {createPlaybackControlState} from "./PlaybackControlState/PlaybackControlStateFactory";
import {IPlaybackControlState} from "./PlaybackControlState/IPlaybackControlStateTypes";
import {getUserPreferencesForControlBar} from "../UserPreferencesForControlBarService";
import {IVolumes} from "../../redux/types/IVolumes";

// Terminology
// Speed: a value from 0 to 1, where 0 is the slowest speed and 1 is the fastest speed.
// Tempo: a value in BPM (beats per minute).
// Recording: A Soundslice containing audio or video
// Recording Index: (integer) This is a zero-based index of a Soundslice recording.

//when increase/decrease speed are called, we modify the tempo by this amount, then calculate new speed
const tempoIncrementAdjustmentBPM: number = 5;
//when backing tracks or videos are present, Soundslice sets limits to how fast/slow a track can be adjusted, due to audio artifacts
const maxSpeed = 1.25;
const minSpeed = .25;

interface IPlayerSettings {
  isKeyboardToggleEnabled: boolean,
  isBackingTrackToggleEnabled: boolean,
  isCountInToggleEnabled: boolean,
  isCurrentMeasureHighlightToggleEnabled: boolean,
  isDemoSelectorEnabled: boolean,
  isFingerNumbersToggleEnabled: boolean,
  isInstantFeedbackToggleEnabled: boolean,
  isKeyboardVisible: boolean,
  isLoopButtonEnabled: boolean,
  isMetronomeToggleEnabled: boolean,
  isNotationLayoutToggleEnabled: boolean,
  isNoteNamesToggleEnabled: boolean,
  isPlayheadToggleEnabled: boolean,
  isProportionalToggleEnabled: boolean,
  isRhythmCountsToggleEnabled: boolean,
  isScrollDirectionToggleEnabled: boolean,
  isTempoSelectorEnabled: boolean,
  isZoomButtonEnabled: boolean,
}

/**
 * Think of this class like a conductor of an orchestra. It is responsible for ensuring that everything starts and
 * stops at the right time. It has a clock that is the source of truth in the lesson player. Here are some of its
 * responsibilities:
 * - Tell metronome to start and stop.
 * - Tell Soundslice to start and stop.
 * - Tell count-in to start and stop.
 * - Tell backing track to start and stop.
 * - Tell note scorer to start
 * - Tell demo notes (audio / video) to start and stop.
 *
 * ----OLD DESCRIPTION----
 * Responsible for listening for various events that should result in notes being played.
 * e.g. when a user presses on keyboard keys, Soundslice notes playing, etc.
 * Keeps track of notes Soundslice has broadcasted from "getNotes".
 * Listens for playback control events so notes are played when 'play' is pressed.
 * Orchestrates count in, metronome, notes playback, and rxjs notes events
 */
class PlaybackOrchestrator {
  //notes player subscribes to many different events. We want to unsubscribe when destroy is called, so all the funcs are stored in an array.
  unsubscribeFuncs: (() => void)[] = [];
  //array of notes referenced during playback.
  notes?: Note[];
  //playback scheduler handles math and optimization behind playing notes
  notesPlaybackScheduler = new NotesPlaybackScheduler();
  //used to schedule and play notes in the notes array
  synthesizer: Synthesizer;
  // Encapsulates isPlaying, isLooping, etc in a class so that it can easily be shared with the UI, thus avoiding redundancies.
  playbackControlState: IPlaybackControlState;
  //you can seek while not playing, so we need to store where to start playing from.
  seekOffsetSeconds: number = 0;
  segment?: Segment;
  //keep track if looping is enabled. -1 indicates it is disabled.
  loopEndOffsetSeconds: number = -1;
  loopStartOffsetSeconds: number = -1;
  //needed so we can pause
  playNotesStartTimeMs: number;
  previouslySelectedBackgroundTrack?: string;
  durationSeconds: number;
  seekToBeginningOnNextSSPauseMessage: boolean; //Soundslice only has pause, so we need to keep track if we intend to stop (start from beginning) or pause.
  nextSSPausePromiseResolve?: ()=> void; //for playing count in at the beginning of a loop.
  skipNextLoopSeek = false; //when the user clicks play while a loop is defined, Soundslice sends us seek and then the play event. We want to ignore the seek when that happens.
  recordingIndexAudioPlayerHash: {[key: number]: AudioPlayer} = {};
  syncSSplayheadWithAudioCurrentTimeIntervalId: number;
  #ignoreSubsequentPauses = false; // Allows us to ignore duplicate pause events from Soundslice.
  stopEmittingProgressEvents: () => void = () => {};

  constructor(){
    this.playbackControlState = createPlaybackControlState();
    this.synthesizer = new Synthesizer();
    this.registerEventListeners();
  }

  /**
   * Listen for computer keyboard press, Soundslice events, etc.
   */
  registerEventListeners(){
    //keep track of all cleanup/unsubscribe funcs so we can call them when our destroy() function is called.
    this.unsubscribeFuncs = [
      subscribeToReceiveMessageFromSoundsliceIframeEvents(this.routeSoundSliceMessage.bind(this)),
      subscribeToSegmentEvents(this.handleSegmentEvent.bind(this)),
      subscribeToNotesEvents(this.handleNotesEvents.bind(this)),
      subscribeToSettingsEvents(this.handleSettingsEvent.bind(this)),
      subscribeToReceiveMessagesFromNativeApp(this.handleNativeAppMessage.bind(this)),
    ];
  }

  handlePlaybackStartedEvent(event: IPlaybackStarted) {
    const isUsingAndroid = deviceInfo.getIsAndroid();
    if (isUsingAndroid && !this.segment?.hasVideo) {
      nativeBridge.sendMessageToNative({
        type: "audio.playback_started",
        seek: event.seekOffsetSeconds,
        speed: event.speed,
        timestamp: Date.now(),
      });
    }
  }

  handleNativeAppMessage(m: FromNativeAppEventTypes){
    // Some of this logic is duplicated in lessonService.onPlayStopPauseButton_clicked(). Would be nice to not duplicate it.
    switch (m.type) {
      case "spaceBarPressed":
        const isPlaying = lessonPlayerStore.getIsPlaying();
        const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();
        const isPlayingOrCountInPlaying = isPlaying || isCountInPlaying;
        const hasVideo = this.segment?.hasVideo ?? false;

        if (!isPlayingOrCountInPlaying) {
          this.onPlay_pressed();
        } else if (isPlayingOrCountInPlaying && hasVideo) {
          this.onPause_pressed();
        } else if (isPlayingOrCountInPlaying && !hasVideo) {
          this.onStop_pressed();
        }
    }
  }

  handleSettingsEvent(m: SettingsEventTypes){
    if (lessonService.isUserOnLessonPage()) {
      if (m.type === "volumesChanged") {
        this.#onVolumesChanges();
      }
    }
  }

  #onVolumesChanges(): void {
    const volumes = settingsStore.getVolumes();
    soundSliceSdk_setVolume(volumes.backingTrack);
    soundSliceSdk_setVolume(volumes.video);
    this.getAudioPlayerBasedOnRecordingIndex(); //sets volume as well.
    this.synthesizer.setVolume(volumes.piano);
  }

  emitProgressChangedEvents(){
    this.stopEmittingProgressEvents();
    const intervalId = setInterval(()=>{
      void this.emitProgressPercentChangedEvent();
    }, 50);

    const seekOffsetInterval = setInterval(()=>{
      void this.emitSeekOffsetAndDurationSecondsChangedEvent();
    }, 1000);

    this.stopEmittingProgressEvents = () => {
      clearInterval(intervalId);
      clearInterval(seekOffsetInterval);
    };
    return () => this.stopEmittingProgressEvents();
  }

  handleNotesEvents(e: NotesEventTypes){
    const seekJumpAmountSeconds= 5;
    switch (e.type) {
      case "seekSkipBack":
        this.onSeekSkipBack(seekJumpAmountSeconds);
        break;
      case "seekSkipForward":
        this.onSeekSkipForward(seekJumpAmountSeconds);
        break;
      case "playbackStarted":
        this.onPlaybackStarted(e);
        break;
    }
  }

  onSeekSkipBack(seekJumpAmountSeconds: number) {
    const seekBackTime = this.seekOffsetSeconds - seekJumpAmountSeconds > 0 ? this.seekOffsetSeconds - seekJumpAmountSeconds : 0;
    soundSliceSdk_seek(seekBackTime);
  }

  onSeekSkipForward(seekJumpAmountSeconds: number) {
    let seekForwardTime = this.seekOffsetSeconds + seekJumpAmountSeconds;
    if(seekForwardTime > this.durationSeconds){
      seekForwardTime = this.durationSeconds;
    }
    soundSliceSdk_seek(seekForwardTime);
  }

  onPlaybackStarted(e: IPlaybackStarted) {
    this.handlePlaybackStartedEvent(e);
  }

  /**
   * Updates video progress bar
   */
  async emitProgressPercentChangedEvent(){
    const seekTime = Math.floor(this.getSeekTimeRelativeToRecordingIndexSpeed());
    const durationSeconds = this.durationSeconds;
    const progressPercent = (durationSeconds > 0 && seekTime > 0 ?  seekTime / durationSeconds : 0);
    emitProgress_changed(progressPercent, seekTime, durationSeconds);
  }
  //for the time displayed to the left and right of progress bar.
  async emitSeekOffsetAndDurationSecondsChangedEvent(){
    const seekTime = Math.floor(this.getSeekTimeRelativeToRecordingIndexSpeed());
    const durationSeconds = this.durationSeconds;
    emitDurationAndSeekOffsetSeconds_changed(durationSeconds, seekTime);
  }

  handleSegmentEvent(message: SegmentEventTypes){
    if (lessonService.isUserOnLessonPage()) {
      switch (message.type) {
        case "segmentChanged":
          return this.setSegment(message.segment);
        case "segmentNotesChanged":
          return this.setNotes(message.notes);
      }
    }
  }

  /**
   * Sets the notesPlayer to use new segment data.
   * Resets default states for demo, metronome, etc.
   * @param segment
   */
  setSegment(segment?: Segment){
    this.videoLoadedPromise = undefined;
    this.videoLoadedResolve = undefined;
    this.cancelLoadingAllAudioPlayers(); //if we are loading a large song, but switch segments while still loading, we want to stop loading the song.
    this.stop();
    this.recordingIndexAudioPlayerHash = {};
    this.seekToBeginningOnNextSSPauseMessage = false; //calling stop sets this to true, which means if we pause on video, it'll get set back to 0 seek the first time it's paused.
    this.stopEmittingProgressEvents(); //we only want to emit progress events for video progress bar.
    this.segment = segment;
    if(this.segment?.hasVideo){
      this.videoLoadedPromise = new Promise((resolve) => {
        this.videoLoadedResolve = resolve;
      });
    }
    void this.setIsLoopEnabled(false);
    //when setSpeed is called, we modify the recording index of segment. start out defaulting to normal always, which allows us to call setSpeed for user preferences
    if(this.segment){
      const normalRecordingOption = this.segment.speedOptions.find(s => s.label === "Normal");
      this.segment.recordingIndex = normalRecordingOption?.recordingIndex || 0;
      this.createAndLoadAudioPlayers();
    }

    this.setTempo(this.segment? this.segment.tempo : 119);
    this.loopStartOffsetSeconds = -1;
    this.loopEndOffsetSeconds = -1;
    this.seekOffsetSeconds = 0;
    this.calculateAndSetDurationSeconds();
    //don't allow playback until all audio etc is loaded. this is re-enabled when notationloaded and audio loaded.
    lessonPlayerStore.setIsPlaybackEnabled(false);
  }

  //TODO: load one audio file at a time. start with current.
  createAndLoadAudioPlayers(){
    if(!this.segment){ return; }
    const speedOptions = this.segment.speedOptions;
    const recordingIndexes = speedOptions.map(s => s.recordingIndex).filter(ri => ri > 0);
    const uniqueRecordingIndexes = [...new Set(recordingIndexes)];

    if(uniqueRecordingIndexes.length > 0){
      uniqueRecordingIndexes.forEach(recordingIndex => {
        const url = speedOptions.find(s => s.recordingIndex === recordingIndex)?.mp3Url || "";
        if(!url) {
          return logger.lessonAudio.error({message: `speed option didn't have mp3url defined.`});
        }
        const audioPlayer = new AudioPlayer(url);
        audioPlayer.load().catch((e) => {
          if (e instanceof DOMException && e.name === "AbortError") {
            return logger.lessonAudio.warn({message: "loading of audio url was aborted"});
          }
          throw e;
        });
        this.recordingIndexAudioPlayerHash[recordingIndex] = audioPlayer;
      });
    }
  }

  /**
   * Make certain controls non-interactive based on segment mode.
   * Send messages to Soundslice to set values of certain controls based on segment mode. e.g. Performance mode should always be speed 1.
   * Note: we do send some user preferences to the iframe via query string params, but those need to be overwritten by what's allowed for certain segment modes.
   * https://playgroundsessions.atlassian.net/wiki/spaces/DEV/pages/846888961/Control+Bar+Default+Settings+and+Persistence
   * https://playgroundsessions.atlassian.net/wiki/spaces/DEV/pages/775946241/Segment+Mode+Behaviors
   */
  setControlBasedOnSegmentMode(shouldSetSpeed: boolean) {
    const userPreferencesFromLocalStorage = getUserPreferencesForControlBar();
    const speedPercent = lessonPlayerStore.getSpeed();

    const settings = this.#getDefaultPlayerSettings(userPreferencesFromLocalStorage);

    lessonPlayerStore.setDemoMode(userPreferencesFromLocalStorage.demoMode);
    this.setNoteNameVisibility(userPreferencesFromLocalStorage.isNoteNamesEnabled);
    this.setFingeringVisibility(userPreferencesFromLocalStorage.isFingeringEnabled);
    this.setIsPlayheadVisibleWhilePlaying(userPreferencesFromLocalStorage.hidePlayheadDuringPlayback);
    this.setIsCurrentMeasureHighlighted(userPreferencesFromLocalStorage.isCurrentMeasureHighlighted);
    this.setPageCount(userPreferencesFromLocalStorage.pageCount);
    this.setNotationLayout(userPreferencesFromLocalStorage.selectedNotationLayout);
    soundSliceSdk_setZoom(lessonPlayerStore.getPlaybackControlState().zoom); // We don't currently send zoom in the URL, so this is the one way SS knows what zoom should be.

    // Since this function is called twice, once when the segment loads and once when the notes & iframe are ready,
    // we need to not call set speed twice in a row, or it'll lead to unintended side effects.
    if (shouldSetSpeed) {
      void this.setSpeed(speedPercent);
    }

    if (this.segment?.mode === SegmentModeEnum.followAlong) {
      this.#adjustSettingsForFollowAlongMode(settings, userPreferencesFromLocalStorage);

      void this.setIsLoopEnabled(false);
      void this.setSpeed(speedPercent);

    } else if (this.segment?.mode === SegmentModeEnum.play) {
      this.#adjustSettingsForPlayMode(settings, userPreferencesFromLocalStorage);

      void this.setIsLoopEnabled(false);
      void this.setSpeed(speedPercent);
      this.setFingeringVisibility(false);
      this.setNoteNameVisibility(false);

    } else if (this.segment?.mode === SegmentModeEnum.perform) {
      this.#adjustSettingsForPerformMode(settings);

      void this.setIsLoopEnabled(false);
      void this.setSpeed(speedPercent);
    }

    this.setIsKeyboardVisible(settings.isKeyboardVisible);

    lessonPlayerStore.setControlBarToggles(settings);
  }

  #getDefaultPlayerSettings(userPreferences: IUserPreferencesForControlBarV2): IPlayerSettings {
    return {
      isKeyboardToggleEnabled: true,
      isBackingTrackToggleEnabled: true,
      isCountInToggleEnabled: true,
      isCurrentMeasureHighlightToggleEnabled: true,
      isDemoSelectorEnabled: true,
      isFingerNumbersToggleEnabled: true,
      isInstantFeedbackToggleEnabled: true,
      isKeyboardVisible: userPreferences.isBirdsEyeKeyboardEnabled,
      isLoopButtonEnabled: true,
      isMetronomeToggleEnabled: true,
      isNotationLayoutToggleEnabled: true,
      isNoteNamesToggleEnabled: true,
      isPlayheadToggleEnabled: true,
      isProportionalToggleEnabled: true,
      isRhythmCountsToggleEnabled: true,
      isScrollDirectionToggleEnabled: true,
      isTempoSelectorEnabled: true,
      isZoomButtonEnabled: true,
    };
  }

  #adjustSettingsForFollowAlongMode(settings: IPlayerSettings, userPreferences: IUserPreferencesForControlBarV2): IPlayerSettings {
    return {
      ...settings,
      isBackingTrackToggleEnabled: false,
      isCountInToggleEnabled: false,
      isDemoSelectorEnabled: false,
      isFingerNumbersToggleEnabled: true,
      isInstantFeedbackToggleEnabled: false,
      isKeyboardVisible: userPreferences.isBirdsEyeKeyboardEnabledForSegmentsWithBothVideoAndNotation,
      isLoopButtonEnabled: false,
      isMetronomeToggleEnabled: false,
      isNotationLayoutToggleEnabled: true,
      isNoteNamesToggleEnabled: true,
      isPlayheadToggleEnabled: true,
      isRhythmCountsToggleEnabled: true,
      isTempoSelectorEnabled: false,
    };
  }

  #adjustSettingsForPlayMode(settings: IPlayerSettings, userPreferences: IUserPreferencesForControlBarV2): IPlayerSettings {
    return {
      ...settings,
      // Disable certain controls for video-only segments
      isBackingTrackToggleEnabled: false,
      isCountInToggleEnabled: false,
      isCurrentMeasureHighlightToggleEnabled: false,
      isDemoSelectorEnabled: false,
      isFingerNumbersToggleEnabled: false,
      isInstantFeedbackToggleEnabled: false,
      isKeyboardVisible: userPreferences.isBirdsEyeKeyboardEnabledForSegmentsWithVideoOnly,
      isLoopButtonEnabled: false,
      isMetronomeToggleEnabled: false,
      isNotationLayoutToggleEnabled: false,
      isNoteNamesToggleEnabled: false,
      isPlayheadToggleEnabled: false,
      isProportionalToggleEnabled: false,
      isRhythmCountsToggleEnabled: false,
      isScrollDirectionToggleEnabled: false,
      isTempoSelectorEnabled: false,
      isZoomButtonEnabled: false,
    };
  }

  #adjustSettingsForPerformMode(settings: IPlayerSettings): IPlayerSettings {
    return {
      ...settings,
      isLoopButtonEnabled: false,
      isDemoSelectorEnabled: false,
      isTempoSelectorEnabled: false,
    };
  }

  calculateAndSetDurationSeconds(){
    const isVideo = this.segment?.mode === SegmentModeEnum.play ?? false;

    if(!this.segment || !this.segment.measures || this.segment.measures.length <= 0) {
      if (!isVideo) {
        logger.lesson.warn({message: `segment has no measures so cannot determine duration`});
      }
      return;
    }

    this.durationSeconds = this.getSegmentStartAndEndTimesWithSlowRecordingIndexAccountedFor().endTime;
  }
  /**
   * Listen for "notationLoadedResponse" and "getNotesResponse" messages from Soundslice so that we have all the notes of piece currently loaded and can play them, etc.
   * @param message - Soundslice message
   */
  async routeSoundSliceMessage(message: SoundsliceResponseEventTypes) {
    switch(message.type){
      case "ssLoopChange":
        this.#onSSLoopChangeMessage_received(message.loopEndOffsetSeconds, message.loopStartOffsetSeconds);
        break;
      case "ssNotationLoaded":
        this.#onSSNotationLoaded();
        break;
      case "ssAudioLoaded":
        this.#onSSAudioLoaded();
        break;
      case "ssPlay":
        this.#onSSPlayMessage_received();
        break;
      case "ssPause":
        this.#onSSPauseMessage_received();
        break;
      case "ssSeek":
        this.#onSSSeekMessage_received(message.offset, message.state);
        break;
      case "ssAudioEnd":
        this.#onSSAudioEndMessage_received();
        return;
      case "ssDuration":
        this.#onSSDurationMessage_received(message.durationSeconds);
        break;
      case "ssOneTimeLoopEnd":
        this.#onSSOnetimeLoopEndMessage_received();
        break;
      case "ssZoom":
        this.#onSSZoomMessage_received(message.zoomLevel);
        break;
    }
  }

  #onSSLoopChangeMessage_received(loopEndOffsetSeconds: number, loopStartOffsetSeconds: number): void {
    logger.lesson.log({message: `non-debounced loopchange is setting loop start and end so `});
    if(loopEndOffsetSeconds === this.loopEndOffsetSeconds && this.loopEndOffsetSeconds === -1 ){ return; }
    void this.handleLoopChange(loopStartOffsetSeconds, loopEndOffsetSeconds);
  }

  async #onSSNotationLoaded(): Promise<void> {
    //start sending messages for setting zoom, etc.,
    // once the notation is loaded, which indicates that the Soundslice iframe is ready.
    this.setControlBasedOnSegmentMode(true);

    if(!this.segment) {
      return logger.lesson.error({message: 'no segment'});
    }
    if(this.segment?.hasVideo){ //&& this.segment.measures.length <= 0  TODO: once Ben fixes measure durations, start using it again.
      logger.lesson.log({message: `segment has no measures, so using Soundslice duration`});
      soundSliceSdk_getDurationAsync();
    }
    soundSliceSdk_seek(0); //move the cursor
    try{
      await this.waitForAllAudioPlayersToLoad();
      logger.lessonAudio.log({message: `all audio has finished loading so enabling playback`});
      lessonPlayerStore.setIsPlaybackEnabled(true);

      const shouldAutoPlay = lessonPlayerStore.getShouldPlayWhenSSReady()

      if (shouldAutoPlay) {
        // Wait a smidgen for async stuff above to propagate otherwise player won't play.
        setTimeout(() => {
          lessonPlayerStore.setShouldPlayWhenSSReady(false);
          play();
        }, 500);
      }

    }catch(e){
      if( e instanceof DOMException && e.name === "AbortError"){
        return logger.lessonAudio.warn({message: `load was aborted so swallowing the error`})
      }
      throw e;
    }
  }

  #onSSAudioLoaded(): void {
    if(this.videoLoadedResolve){
      logger.lesson.log({message: `video loaded`});
      this.videoLoadedResolve();
      this.videoLoadedResolve = undefined;
      this.videoLoadedPromise = undefined;
    }
  }

  async #onSSPlayMessage_received(): Promise<void> {
    //we need to listen for Soundslice to send play, rather than start playing as soon as our event fires, as Soundslice can take a second or so to start playing.
    this.#ignoreSubsequentPauses = false;
    if(this.segment?.hasVideo){
      logger.lesson.log({message: `received ssPlay for a video and we weren't already playing so playing notes.`});
      this.seekOffsetSeconds = await soundSlice.getCurrentTime();
      logger.lesson.log({message: `video current time: ` + this.seekOffsetSeconds});
      void this.playNotes(true);
    }
  }

  #onSSPauseMessage_received(): void {
    if (this.#ignoreSubsequentPauses) {
      return;
    }
    // Sometimes we send duplicate "pause" events to Soundslice.
    // This can be troublesome when the user is tapping the screen to seek to a part of the notation.
    // Here's how.
    // If we don't ignore subsequent pauses until the next "ssPlay" event, the following sometimes happens:
    // 1. User taps the screen to seek to a part of the notation.
    // 2. We send a pause, seek, then a play to the Soundslice iframe, in that order.
    // 3. Soundslice replies with a pause, seek, play, then pause.
    // 4. The Soundslice playhead continues to move, but our stops playing, based on the last pause event.
    // Ignoring subsequent pauses until the next "ssPlay" event prevents this.
    this.#ignoreSubsequentPauses = true;
    nativeBridge.sendMessageToNative({type: 'audio.stopped', timestamp: Date.now()});

    const shouldScore = this.isCursorNearTheEndOfTheSegment();
    void this.stopNotes(shouldScore); //we need to stop here because ss sends a seek, pause, then audioEnded. the seek causes us to schedule a note (like hihat) right before end, which results in a double tss when looping is enabled.
  }

  #onSSSeekMessage_received(offset: number, state?: ISoundSliceRequestContext): void {
    if(state){
      return logger.lesson.log({message: `ignoring Soundslice message with state as that has been invoked by this class, which is awaiting the promise.`});
    }
    this.seek(offset);
  }

  #onSSAudioEndMessage_received(): void {
    return;
  }

  #onSSDurationMessage_received(durationSeconds: number): void {
    this.durationSeconds = durationSeconds;
  }

  #onSSOnetimeLoopEndMessage_received(): void {
    this.handleOneTimeLoopEnd();
  }

  #onSSZoomMessage_received(zoomLevel: number): void {
    lessonPlayerStore.setZoom(zoomLevel);
  }

  videoLoadedResolve?: ()=> void;
  videoLoadedPromise?: Promise<void>
  async waitForAllAudioPlayersToLoad(){
    if(this.videoLoadedPromise){
      return this.videoLoadedPromise;
    }
    return new Promise<void>(async (resolve, reject)=>{
      const promises = Object.entries(this.recordingIndexAudioPlayerHash).map(([_, audioPlayer])=> audioPlayer.load());
      try{
        void await Promise.all(promises);
        resolve();
      }catch(e){
        logger.lessonAudio.error({message: `error in loading audio players`});
        reject(e);
      }
    });
  }

  cancelLoadingAllAudioPlayers(){
    Object.entries(this.recordingIndexAudioPlayerHash).map(([_, audioPlayer])=> audioPlayer.cancelLoad());
  }

  /**
   * When play_type=2 query string param is used, Soundslice will not automatically start loops over, which allows us to better control our
   * custom loop behavior.
   * Event order when loop goes to end of segment: ssPause, ssOneTimeLoopEnd, ssPause
   * Event order when loop doesn't go to the end of the segment: ssOneTimeLoopEnd, ssPause
   * We want to wait until after that ssPause to do anything, so we will use a setTimeout.
   */
  handleOneTimeLoopEnd(){
    logger.lesson.log({message: 'one time loop==========================================='})
    setTimeout(()=>{
      this.#play(true);
    }, 10);
    nativeBridge.sendMessageToNative({type: 'audio.stopped', timestamp: Date.now()});
  }

  async handleLoopChange(loopStartOffsetSeconds: number, loopEndOffsetSeconds: number){
    logger.lesson.log({message: `handleLoopChange: ${loopStartOffsetSeconds}`});
    this.loopStartOffsetSeconds = loopStartOffsetSeconds;
    this.loopEndOffsetSeconds = loopEndOffsetSeconds;
    if(!this.isLoopDefined()){
      void this.setIsLoopEnabled(false, false); //if the user cleared the loop by clicking somewhere, we want our loop button to reflect that there is no loop defined.
    }else{
      void this.setIsLoopEnabled(true, false);

      // stop notes, countin, etc.
      this.stop(false);
      //if the current time isn't at the beginning of the loop, seek to the beginning of the loop.
      const currentTime = await soundSlice.getCurrentTime();
      const shouldSeekBecauseCurrentTimeIsntLoopStartTime = currentTime != loopStartOffsetSeconds;
      if(shouldSeekBecauseCurrentTimeIsntLoopStartTime){
        logger.lesson.log({message: `telling ss to seek to loop beginning: ${this.loopStartOffsetSeconds}`});
        soundSliceSdk_seek(this.loopStartOffsetSeconds);
      }
    }
  }
  setNotes(notes?: Note[]){
    this.notes = notes;
  }

  /**
   * Asks Soundslice to start playing, which will notify us when it's starts playing, so playNotes can execute.
   */
  #play(onlyPlaySetGoForCountIn = false){
    // Make sure all the things that use volumes are synced up with current volumes.
    this.#onVolumesChanges();

    logger.lesson.log({message: `play`});
    clearAllNoteFeedback();
    //if there's a loop, seek to the beginning, in case the user has dragged the end of the loop below the fold, we want to scroll up before playing starts.
    if(this.isLoopDefined()) {
      soundSliceSdk_seek(this.loopStartOffsetSeconds);
    }
    this.playCountInIfApplicable(onlyPlaySetGoForCountIn);
  }

  isLoopDefined(){
    return this.loopEndOffsetSeconds !== -1;
  }

  /**
   * Stop playback of notes and ask Soundslice to stop scrolling.
   */
  stop(seek = true){
    this.emitIsPlaying_changed(false);
    this.setIsCountInPlaying(false);
    void this.stopNotes();
    countIn.stop();
    this.seekToBeginningOnNextSSPauseMessage = seek;
    soundSliceSdk_pause();
    nativeBridge.sendMessageToNative({type: 'audio.stopped', timestamp: Date.now()});
  }

  /**
   * Pause is used for video segments only. Non-video segments use stop.
   * Pause the song by telling Soundslice to pause, and then seeking to the current seektime.
   */
  pause() {
    this.stopEmittingProgressEvents();
    this.emitIsPlaying_changed(false);
    this.stopCountIn();
    this.seekToBeginningOnNextSSPauseMessage = false;
    soundSliceSdk_pause();
  }

  getSeekTimeRelativeToRecordingIndexSpeed(speed=this.playbackControlState.speed){
    if(!this.playbackControlState.isPlaying){ return this.seekOffsetSeconds; }
    const playingDuration = (performance.now() - this.playNotesStartTimeMs) / 1000;
    const speedRelativePlayDuration = playingDuration * speed;
    const newSeek = this.seekOffsetSeconds + speedRelativePlayDuration;
    return newSeek;
  }

  getSegmentStartAndEndTimesWithSlowRecordingIndexAccountedFor(seekTime = this.getSeekTimeRelativeToRecordingIndexSpeed()){
    if(!this.segment || !this.segment.measures){
      return {startTime: 0, endTime: 0};
    }

    let durationTally = 0;
    for(const measure of this.segment.measures){
      const duration = measure.durationSeconds;
      durationTally += duration;
    }
    return {startTime: seekTime, endTime: durationTally};
  }

  /**
   * Play notes currently loaded into our notes property, which is populated by handleReceiveMessageFromSoundslice ssGetNotes.
   */
  private async playNotes(skipTellingIframeToPlay = false) {
    if (!lessonService.isUserOnLessonPage()) {
      return;
    }

    logger.lessonAudio.debug({
      message: `playNotes called.`,
      data: [
        { key: "seekOffset", value: this.seekOffsetSeconds },
        { key: "loopEndOffset", value: this.loopEndOffsetSeconds },
        { key: "skipTellingIframeToPlay", value: skipTellingIframeToPlay }
      ]
    });

    emitNotesCurrentlySelected_changed([]);//reset birdseye keyboard
    if(!this.segment) {
      return;
    }

    if(!this.notes) {
      // TODO: use notes loaded event to enable the play button
      logger.lessonAudio.warn({message: `notes not loaded yet, so play can't be pressed.`})
      return ;
    }
    this.emitProgressChangedEvents();
    this.emitIsPlaying_changed(true);//triggers ui update

    // If there is a backing track, start playing it.
    const audioPlayer = this.getAudioPlayerBasedOnRecordingIndex();

    if(audioPlayer) {
      await audioPlayer.play();
    }

    const speed = this.playbackControlState.speed;
    lessonPlayerStore.setScoringStartTimeInMs(getAudioContext().currentTime * 1000);
    emitPlayback_started(this.segment, this.notes, this.playbackControlState.speed, speed, this.seekOffsetSeconds, this.loopEndOffsetSeconds, true);
    //tell the note scorer / pitch detector that we've started.
    lessonPlayerStore.setScoringStartTimeInMs(getAudioContext().currentTime * 1000);

    void this.resetPianoDemoSchedule();
    this.playMetronomeIfApplicable();
    this.playNotesStartTimeMs = performance.now();

    if(!skipTellingIframeToPlay){ //when a video is touched/clicked, we will get ssPlay and then call playNotes(true)
      logger.lesson.log({message: `playNotes is telling iframe to play`});
      this.tellIframeToPlay();
    }
  }

  async resetPianoDemoSchedule(){
    if(!this.notes){
      logger.lessonAudio.warn({message: `notes not loaded yet, so demo can't be played.`})
      return ;
    }

    const audioPlayer = this.getAudioPlayerBasedOnRecordingIndex();
    const speed = this.playbackControlState.speed;
    const speedOptions = this.segment?.speedOptions || [];
    const speedForRecordingIndex = speedOptions.find(s => s.recordingIndex === this.segment?.recordingIndex)?.value || 1;

    const getAudioCurrentTime = audioPlayer ? () => {
      const currentTime = audioPlayer.getCurrentTime();
      const currentTimeRelativeToBackingTrack = currentTime * speedForRecordingIndex;
      return currentTimeRelativeToBackingTrack;
    } : undefined;

    // Schedule playing of notes as well as painting the birdseye keyboard.
    // Only do this for notes for the given piano demo type (left, right, both, none)
    const notesForDemoMode = getNotesForDemoMode(this.notes, lessonPlayerStore.getDemoMode());
    this.#scheduleNotesForDemoSoundIfApplicable(this.notes, speed, getAudioCurrentTime);
    this.#scheduleNotesForDemoVisualsIfApplicable(notesForDemoMode, speed);
  }

  #scheduleNotesForDemoVisualsIfApplicable(notes: Note[], speed: number) {
    // with followAlong, we always want all the notes highlighted.
    const adjustedNotes = this.segment?.mode === "followAlong" ? (this.notes ?? notes) : notes;
    notesEventWorker.scheduleNoteEvents(adjustedNotes, speed, this.seekOffsetSeconds, this.loopEndOffsetSeconds);
  }

  #scheduleNotesForDemoSoundIfApplicable(allNotes: Note[], speed: number, getAudioCurrentTime?: () => number) {
    const segment = lessonPlayerStore.getSegment();
    let canPlayDemo = true;

    // Guard: Don't play demo sound if the segment is a video, follow along, or a performance type.
    if (segment) {
      canPlayDemo = (segment.mode !== SegmentModeEnum.followAlong && segment.mode !== SegmentModeEnum.play && segment.mode !== SegmentModeEnum.perform);
    }

    if (!canPlayDemo) {
      return;
    }

    // Figure out which notes to play based on the demo sound settings.
    const demoSoundMode = lessonPlayerStore.getDemoSoundMode();
    const notesToDemo= getNotesForDemoMode(allNotes, demoSoundMode);

    const playbackOptions = {
      notes: notesToDemo,
      speed,
      synthesizer: this.synthesizer,
      seekOffsetSeconds: this.seekOffsetSeconds,
      loopEndOffsetSeconds: this.loopEndOffsetSeconds,
      getAudioCurrentTime
    };

    this.notesPlaybackScheduler.scheduleNotesPlayback(playbackOptions);
  }

  stopSyncingSSPlayhead(){
    window.clearInterval(this.syncSSplayheadWithAudioCurrentTimeIntervalId);
    this.syncSSplayheadWithAudioCurrentTimeIntervalId = -1;
  }

  pauseAllAudioPlayers(){
    Object.entries(this.recordingIndexAudioPlayerHash).forEach(([_, audioPlayer]) => audioPlayer.pause());
  }
  getAudioPlayerBasedOnRecordingIndex(){
    if(!this.segment) {
      return logger.lesson.error({message: `no segment to get audioPlayer for`});
    }

    const audioPlayer = this.recordingIndexAudioPlayerHash[this.segment.recordingIndex];
    if(audioPlayer){
      const volume = !deviceInfo.getIsAndroid() ? settingsStore.getBackingTrackVolume() : 0;
      audioPlayer.setVolume(volume);
    }
    return audioPlayer;
  }

  /**
   * If there is no looping, and we are at the beginning of the song, play the count in.
   * Otherwise, immediately start playing the song.
   */
  playCountInIfApplicable(onlyPlaySetGoForCountIn = false){
    const isCountInMuted = settingsStore.getIsCountInMuted()

    countIn.setVolume(settingsStore.getCountInVolume());

    let isSegmentTypeFollowAlongOrVideo = false;
    const segment = lessonPlayerStore.getSegment();
    if (segment) {
      isSegmentTypeFollowAlongOrVideo = segment.mode === SegmentModeEnum.followAlong || segment.mode === SegmentModeEnum.play;
    }

    if (isSegmentTypeFollowAlongOrVideo) {
      // segment has a video so telling iframe to play, which will then call playNotes
      this.tellIframeToPlay();
      return;
    }

    if (isCountInMuted) {
      // When count in is muted, still play "set, go"
      void this.playCountIn(true);
    } else {
      void this.playCountIn(onlyPlaySetGoForCountIn);
    }
  }

  #endPlaybackIfCurrentlyPlaying(): void {
    const isPlaying = lessonPlayerStore.getIsPlaying();
    const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();

    if (isPlaying || isCountInPlaying) {
      emitPlayback_ended(false);
    }
  }

  playCountIn(onlyPlaySetGo = false){
    //temporary check until backend starts supplying all needed data.
    if(this.segment === undefined || this.segment.measures === undefined || this.segment.measures.length <= 0){
      void this.playNotes();
      logger.lesson.error({message: `can't play count-in without segment measures`});
      return;
    }

    const firstMeasure = this.segment.measures[0];
    const barDurationSeconds = firstMeasure.durationSeconds;

    const {beats: beatsPerMeasure, beatType} = firstMeasure;
    if(beatsPerMeasure === undefined || beatType === undefined) {
      logger.lesson.error({message: `can't play count-in without beatsPerMeasure or beatType defined in the first measure.`});
      return;
    }

    this.#endPlaybackIfCurrentlyPlaying();

    this.setIsCountInPlaying(true);

    void countIn.play({
      speed: this.playbackControlState.speed,
      barDurationSeconds,
      beatType,
      beatsPerMeasure,
      onlyPlaySetGo,
    })
      .then(() => {
        void this.playNotes();
        this.setIsCountInPlaying(false);
      });

    // These offsets are turned into a schedule the moment the "play" button is pressed.

    const ticks = metronome.createHihatNotesForMeasures(this.segment.measures).map((note) => {
      return {
        offsetSeconds: note.offset,
        gain: (note.velocity) ? note.velocity/127 : 1
      };
    });

    if(!this.notes){
      // TODO: (copied from above) use notes loaded event to enable the play button
      logger.lessonAudio.warn({message: `notes not loaded yet, so play can't be pressed.`})
      return ;
    }

    const pianoDemoOffsets = getNotesForDemoMode(this.notes, "both hands")
      .filter(note => !note.tieEnd)
      .map((note): INote => {
        return {
          pitch: note.midi,
          offsetSeconds: note.offset,
          durationSeconds: note.duration,
          isPlayedWithRightHand: note.hand === "right hand",
        };
      });

    // Send offsets to native-land to be scheduled there when the "play" button is pressed.
    nativeBridge.sendMessageToNative({type: "audio.pianoDemoOffsets_created", notes: pianoDemoOffsets, timestamp: Date.now()});
    nativeBridge.sendMessageToNative({type: "audio.metronomeOffsets_created", ticks, timestamp: Date.now()});

    // TODO evaluate if we can remove this.
    // this.emitPianoDemoMode_changed();

    // const demoMode = this.playbackControlState.demoMode;
    // const isRightHand = demoMode === "right hand" || demoMode === "both hands";
    // const isLeftHand = demoMode === "left hand" || demoMode === "both hands";
    // nativeBridge.sendMessageToNative({type: "audio.pianoDemoMode_changed", isRightHand, isLeftHand, timestamp: Date.now()});

    //SoundTouch doesn't have a good timing mechanism, so we have to calculate how long it's going to take for audio to start playing after we tell SoundTouch to play.
    //since this can take ~200ms, we can start calculating now if it hasn't been calculated before, that way there's no 200ms gap before audio starts playing.
    void this.determineAmountOfTimeItTakesForAudioToStartForAllAudioPlayers();
  }
  async determineAmountOfTimeItTakesForAudioToStartForAllAudioPlayers(){
    if(!this.segment) {
      return logger.lesson.error({message: 'no segment'});
    }

    //do the current segment first
    const audioPlayer = this.recordingIndexAudioPlayerHash[this.segment.recordingIndex];
    if(audioPlayer){
      await audioPlayer.determineAmountOfTimeItTakesForAudioToStart();
    }

    //then do remaining
    const promises = Object.entries(this.recordingIndexAudioPlayerHash).map(async ([_, audioPlayer]) => {
      return audioPlayer.determineAmountOfTimeItTakesForAudioToStart();
    });
    return Promise.all(promises);
  }

  /**
   * Plays the metronome sound. Because of the way the metronome notes have to be scheduled in advance, "muting" the
   * metronome is done by setting the volume to 0. This is done in the metronome class. So from the perspective of any
   * class outside the metronome, the metronome will always play. No need to try to call stop() on the metronome.
   */
  playMetronomeIfApplicable() {
    if (!lessonService.isUserOnLessonPage()) {
      return;
    }

    if(this.segment === undefined) {
      return logger.lesson.error({message: `can't play metronome without a segment`});
    }

    const canPlayMetronome = this.segment.mode !== SegmentModeEnum.followAlong && this.segment.mode !== SegmentModeEnum.play;
    if (!canPlayMetronome) {
      return;
    }

    // Set the initial volume - the store factors in if the metronome is muted or not. Volume / muteness can be changed
    // later while segment is playing.
    metronome.setVolume(settingsStore.getMetronomeVolume());

    const audioPlayer = this.getAudioPlayerBasedOnRecordingIndex();
    const speedOptions = this.segment?.speedOptions || [];
    const speedForRecordingIndex = speedOptions.find(s => s.recordingIndex === this.segment?.recordingIndex)?.value || 1;
    const getAudioCurrentTime = audioPlayer ? () => {
      const currentTime = audioPlayer.getCurrentTime();
      const currentTimeRelativeToBackingTrack = currentTime * speedForRecordingIndex;
      return currentTimeRelativeToBackingTrack;
    } : undefined;

    metronome.play({
      segment: this.segment,
      speed: this.playbackControlState.speed,
      seekOffsetSeconds: this.seekOffsetSeconds,
      loopEndOffsetSeconds: this.loopEndOffsetSeconds,
      getAudioCurrentTime
    });
  }

  stopCountIn(): void {
    this.setIsCountInPlaying(false);
    countIn.stop();
  }

  setIsCountInPlaying(isCountInPlaying: boolean){
    this.playbackControlState.isCountInPlaying = isCountInPlaying;
    lessonPlayerStore.setIsCountInPlaying(this.playbackControlState.isCountInPlaying);
  }
  /**
   * Called when ssPause is received.
   * Stops note and metronome playback
   */
  dontSeekNextStop = false;
  private async stopNotes(shouldScore: boolean = true){
    this.stopSyncingSSPlayhead();

    if (!lessonService.isUserOnLessonPage()) {
      return;
    }

    if(!this.segment) {
      return;
    }

    logger.lesson.log({message: `stopNotes called`});
    this.setIsCountInPlaying(false);

    //Soundslice can send ssPause at silly times. like at the end of a loop, or right before the end of a song. So we need to determine if we should wait for scoring (and potentially display score modal)
    const shouldWaitForScoringTcComplete = !this.isLoopDefined() && this.isCursorNearTheEndOfTheSegment();
    emitPlayback_ended(shouldWaitForScoringTcComplete, shouldScore);

    if(this.dontSeekNextStop) {
      this.dontSeekNextStop = false;
    } else if(this.seekToBeginningOnNextSSPauseMessage) {
      this.seekToBeginningOnNextSSPauseMessage = false;
      const seekTime = this.isLoopDefined() ? this.loopStartOffsetSeconds : 0;
      logger.lesson.log({message: `stopNotes is seeking to: ` + seekTime, data: [ { key: "isLoopedDefined", value: this.isLoopDefined() }] });
      soundSliceSdk_seek(seekTime);
    }else{
      this.seekOffsetSeconds = this.getSeekTimeRelativeToRecordingIndexSpeed(); //emit progress for video progress bar needs the seekOffsetSeconds to be updated
    }
    this.emitIsPlaying_changed(false); //triggers ui update

    this.pauseAllAudioPlayers();
    const audioPlayer = this.getAudioPlayerBasedOnRecordingIndex();
    if(audioPlayer) {
      await audioPlayer.pause();
    }
    this.notesPlaybackScheduler.cancelAllScheduledNotesPlayback();
    notesEventWorker.cancelScheduledNoteEvents();
    metronome.stop();
    if(this.nextSSPausePromiseResolve !== undefined) {
      this.nextSSPausePromiseResolve();
    }

    this.seekOffsetSeconds = await soundSlice.getCurrentTime(); //update our seek time so progress bar is accurate.
    this.stopEmittingProgressEvents();
  }

  isCursorNearTheEndOfTheSegment(){
    const seekTime = this.getSeekTimeRelativeToRecordingIndexSpeed();
    const {endTime} = this.getSegmentStartAndEndTimesWithSlowRecordingIndexAccountedFor();
    const delta = Math.abs(seekTime - endTime);
    const isAtEndOfSong = delta < .3; //give some wiggle room of 300ms
    logger.lesson.log({message: `isAtEndOfSong: ${isAtEndOfSong} seekTime: ${seekTime} endTime: ${endTime} delta: ${delta}`});
    return isAtEndOfSong;
  }

  tellIframeToPlay(){
    logger.lesson.log({message: `tellIframeToPlay`});
    //when the user clicks play when a loop is defined, the iframe sends us a seek, then play event. When that happens, we want to ignore that seek event/
    if(this.isLoopDefined()){
      this.skipNextLoopSeek = true;
    }
    this.emitIsPlaying_changed(true);
    logger.lesson.info({
      message: `sending message to start soundslice`,
      data: [
        { key: "currentTime", value: getAudioContext().currentTime }
      ]
    });
    soundSliceSdk_play();
  }

  /**
   * Change the position of where audio is playing in the song/segment.
   * Seek logic is somewhat complex due to needing to retain context of why seek is happening, as seek happens when user presses play, when user presses stop, and when loop restarts.
   * @param offset - seconds into the song that playback should go to.
   */
  skipNextSeekEntirely: boolean = false;
  async seek(offset = 0){
    if (!lessonService.isUserOnLessonPage()) {
      return;
    }

    if(this.skipNextSeekEntirely) {
      return this.skipNextSeekEntirely = false;
    }
    emitNotesCurrentlySelected_changed([]);//reset
    logger.lesson.log({message: `seek: ${offset}`});
    this.seekOffsetSeconds = offset;
    const isSeekDoneAsPartOfLoop = this.isSeekDoneAsPartOfLoop(this.seekOffsetSeconds);
    if(!isSeekDoneAsPartOfLoop){
      logger.lesson.log({message: `seek was not done as part of loop.`});
      this.nextSSPausePromiseResolve = undefined;
      this.skipNextLoopSeek = false;
      countIn.stop(true);
    }

    //the offset passed into this function is based on normal speed & duration. We need to convert it to be relative to the audio track we are using (ie. recording index)
    const audioPlayer = this.getAudioPlayerBasedOnRecordingIndex();
    if(audioPlayer && !this.skipNextLoopSeek){
      await audioPlayer.pause();
      //only convert between recording indexes.
      const speedOptions = this.segment?.speedOptions || [];
      const speedOptionForRecordingIndex = speedOptions.find(s => s.recordingIndex === this.segment?.recordingIndex);//Note: this will be wrong if Very Slow comes before Slow.
      if(speedOptionForRecordingIndex){
        const offsetRelativeToSpeed = offset / speedOptionForRecordingIndex.value;
        await audioPlayer.setCurrentTime(offsetRelativeToSpeed);
      }
    }
    //if we're currently playing, stop and restart playback from the seek position
    if(this.playbackControlState.isPlaying){
      if(!this.skipNextLoopSeek) { //if we tell Soundslice to play when loop is defined, they send ssSeek then ssPlay. We want to ignore that ssSeek, so we set this flag before we tell them to play.
        //we want the count-in to play when the loop starts over.
        logger.lesson.log({message: `seek called, no skip and is playing. isSeekDoneAsPartOfLoop: ${isSeekDoneAsPartOfLoop}`});
        //stop playback, seek, then play.
        if(isSeekDoneAsPartOfLoop) {
          logger.lesson.log({message: `seek was part of loop, so restarting playback and playing beginning of countin.`});
          this.dontSeekNextStop = true;
          const nextPausePromise = new Promise<void>((resolve) => { this.nextSSPausePromiseResolve = resolve;});
          nextPausePromise.then(() => {
            soundSliceSdk_seek(this.loopStartOffsetSeconds); //even though the seek is for the beginning of the loop, Soundslice doesn't move the cursor to the beginning.
            if(audioPlayer){
              audioPlayer.setCurrentTime(this.loopStartOffsetSeconds);
            }
            this.#play(true);
          });
          soundSliceSdk_pause();
        } else {
          // We need to tell Soundslice to pause, then play, so we can start our audio (backing tracks, count in, piano demo, metronome)
          // before the Soundslice playhead starts moving. This only applies for notation segments.
          let shouldPausePlayback = false;
          if (this.segment) {
            shouldPausePlayback = (this.segment.mode !== SegmentModeEnum.followAlong && this.segment.mode !== SegmentModeEnum.play);
          }

          if (shouldPausePlayback) {
            logger.lesson.log({message: `seek done while playing and no loop defined.`});
            this.dontSeekNextStop = true;
            const nextPausePromise = new Promise<void>((resolve) => {
              this.nextSSPausePromiseResolve = resolve;
            });
            nextPausePromise.then(() => {
              if (this.segment?.hasVideo && offset == 0) {
                // The video has ended. Don't start playing the video after SS seeks to the beginning.
              } else {
                this.playNotes();//this will tell iframe to play.
              }
            });
            soundSliceSdk_pause();
          }
        }
      }else{
        logger.lesson.log({message: `skipping seek`});
        this.skipNextLoopSeek = false;
      }
    }else{
      const notesCurrentlySelected = findNotesAtOffset(this.notes, offset);
      emitNotesCurrentlySelected_changed(notesCurrentlySelected);
    }
    void this.emitProgressPercentChangedEvent(); //update the progress bar when paused.
    void this.emitSeekOffsetAndDurationSecondsChangedEvent(); //update the progress bar immediately, rather than wait for interval
  }

  async setSpeed(speed: number) {
    if (!lessonService.isUserOnLessonPage()) {
      return;
    }
    if(!this.segment) {
      return logger.lesson.warn({message: `segment is undefined, so no tempo to modify speed for.`});
    }
    const speedRelativeToRecordingIndex = this.getSpeedRelativeToRecordingIndex(speed);//slow would be .5 if using the normal backing track, but since we switch to the slow backing track, it's relative speed is 1.
    const newSeekTime = this.calculateNewSeekTimeWhenSpeedIsSet(speed);

    //set our speed, tempo, and update the ui.
    this.playbackControlState.speed = speed;
    lessonPlayerStore.setSpeed(this.playbackControlState.speed); //update the ui
    this.setTempo(this.segment.tempo * speed);//update our tempo displayed in the ui

    const audioPlayer = this.getAudioPlayerBasedOnRecordingIndex();
    if(audioPlayer) {
      audioPlayer.setPlaybackRate(speedRelativeToRecordingIndex);
    }

    const speedOptions = this.segment.speedOptions;
    const mp3Url = speedOptions.find(s => s.recordingIndex === this.segment?.recordingIndex)?.mp3Url || "";
    const url = (audioPlayer && !audioPlayer.shouldUseShifter) ? mp3Url : "";
    if (url !== this.previouslySelectedBackgroundTrack) {
      const normalOption = speedOptions.find(s => s.value === 1);
      const isNormalTempo = normalOption && normalOption.mp3Url === url;
      const trackType = (function() { // iife
        if (!url) { return "none"; }
        return isNormalTempo ? "normal" : "slow";
      })();
      nativeBridge.sendMessageToNative({type: "audio.backingTrack_selected", trackType, timestamp: Date.now()})
      this.previouslySelectedBackgroundTrack = url;
    }

    logger.lesson.log({message: `telling iframe to set speed: ${speed}`});
    soundSliceSdk_setSpeed(speed);

    if(this.isLoopDefined()){
      soundSliceSdk_seek(this.loopStartOffsetSeconds);
    }else{
      logger.lesson.log({message: `telling iframe to seek to : ${newSeekTime}`});
      soundSliceSdk_seek(newSeekTime);
    }
  }

  calculateNewSeekTimeWhenSpeedIsSet(speed: number){
    if(!this.segment) {
      return this.seekOffsetSeconds;
    }

    const previousRelativeSpeed = this.playbackControlState.speed;
    let newSeekTime = this.getSeekTimeRelativeToRecordingIndexSpeed(previousRelativeSpeed);

    // find the speed option closest to our current speed, so we can determine if we need to switch backing tracks/recording index
    const speedOptionWithClosestSpeed = this.getSpeedOptionWithClosestSpeed(this.segment, speed);
    if(speedOptionWithClosestSpeed.recordingIndex !== this.segment.recordingIndex){
      logger.lesson.log({message: `recording index doesn't match so calculating new seek time`});
      this.pauseAllAudioPlayers(); // since we are switching audio players, pause the current one so it doesn't keep playing
      this.segment.recordingIndex = speedOptionWithClosestSpeed.recordingIndex;
      //e.g. when going to normal, we multiply by .5. When going to slow, we multiply by 1/.5 == 2
      if(!this.playbackControlState.isPlaying){
        newSeekTime = this.seekOffsetSeconds;
      }else{
        const duration = (performance.now() - this.playNotesStartTimeMs) / 1000 * previousRelativeSpeed;
        const seekTime = this.seekOffsetSeconds;
        newSeekTime = seekTime + duration;
      }
      //recalculate the duration of the segment so progress events work.
      this.calculateAndSetDurationSeconds();
    }
    // console.log(`setSpeed: ${speed} speedRelativeToRecordingIndex: ${speedRelativeToRecordingIndex} previousRelativeSpeed: ${previousRelativeSpeed}`);
    return newSeekTime;
  }

  /**
   * Loop event results in seek as the playback starts over. We need to detect when a user seek is initiated.
   * @param seekOffset
   */
  isSeekDoneAsPartOfLoop(seekOffset: number){
    const roundedSeekOffset = seekOffset.toFixed(4);
    const roundedStartTime = this.loopStartOffsetSeconds.toFixed(4);
    const roundedEndTime = this.loopEndOffsetSeconds.toFixed(4);
    return roundedSeekOffset == roundedEndTime || roundedSeekOffset == roundedStartTime;
  }

  onPlay_pressed(): void {
    this.nextSSPausePromiseResolve = undefined; // if the user clicks stop during a loop, don't execute next resolve.
    this.#play();
  }

  onStop_pressed(): void {
    this.nextSSPausePromiseResolve = undefined; // if the user clicks stop during a loop, don't execute next resolve.
    this.stop();
  }

  onPause_pressed(): void {
    this.nextSSPausePromiseResolve = undefined; // if the user clicks stop during a loop, don't execute next resolve.
    this.pause();
  }

  emitIsPlaying_changed(isPlaying: boolean){
    this.playbackControlState.isPlaying = isPlaying;
    lessonPlayerStore.setIsPlaying(this.playbackControlState.isPlaying);
  }

  setIsPlayheadVisibleWhilePlaying(isPlayheadVisible: boolean) {
    if (isPlayheadVisible) {
      soundSliceSdk_setPlayheadType(4);
    } else if (!isPlayheadVisible) {
      soundSliceSdk_setPlayheadType(1);
    }
    lessonPlayerStore.setHidePlayheadWhilePlaying(isPlayheadVisible);
  }

  setIsCurrentMeasureHighlighted(highlightCurrentMeasure: boolean) {
    soundSliceSdk_setHighlightCurrentBar(highlightCurrentMeasure);
    lessonPlayerStore.setHighlightCurrentMeasure(highlightCurrentMeasure);
  }

  setIsProportionalNotationEnabled(isNotationProportional: boolean) {
    const lessonId = lessonPlayerStore.getActiveLessonId();
    if (lessonId) {
      // Update redux
      const lessonSpacing = lessonPlayerStore.getAllLessonSpacing();

      // Whether or not proportional notation is enabled for a lesson is determined by whether or not there is a spacing
      // entry for the given lesson.
      if (isNotationProportional) {
        const spacingEntry = lessonSpacing.find((entry) => entry.lessonId === lessonId);
        const spacing = spacingEntry ? spacingEntry.spacing : lessonPlayerStore.getDefaultProportionalSpacing();
        const newLessonSpacing = arrayUpsert({
          array: lessonSpacing,
          item: { lessonId, spacing },
          compareFn: (a, b) => a.lessonId === b.lessonId,
        });
        lessonPlayerStore.setSpacingForActiveLesson(spacing);
        lessonPlayerStore.setAllLessonSpacing(newLessonSpacing);

      } else {
        const newLessonSpacing = arrayRemove({
          array: lessonSpacing,
          item: { lessonId, spacing: 0 },
          compareFn: (a, b) => a.lessonId === b.lessonId,
        });
        lessonPlayerStore.setSpacingForActiveLesson(undefined);
        lessonPlayerStore.setAllLessonSpacing(newLessonSpacing);
      }

      // Update Soundslice
      soundSliceSdk_setProportionalNotation(isNotationProportional);
    }
  }

  setNotationSpacing(spacing: number) {
    soundSliceSdk_setProportionalSize(spacing);
    lessonPlayerStore.setSpacingForActiveLesson(spacing);
  }

  setNotationLayout(layout: NotationLayoutEnum) {
    const isPlaying = lessonPlayerStore.getIsPlaying();
    const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();
    const hasPlaybackStarted = isPlaying || isCountInPlaying;

    lessonPlayerStore.setSelectedNotationLayout(layout);

    if (!hasPlaybackStarted) {
      const appliedLayout = getNotationLayoutToApplyFromSelectedNotationLayout();
      soundSliceSdk_setNotationLayout(appliedLayout);
      lessonPlayerStore.setAppliedNotationLayout(appliedLayout);
    }
  }

  setPageCount(pageCount: number) {
    soundSliceSdk_setPagesPerScreen(pageCount);
    lessonPlayerStore.setPageCount(pageCount);
  }

  setIsKeyboardVisible(isVisible: boolean){
    lessonPlayerStore.setFretboardIsVisible(isVisible);
  }

  toggleIsLoopEnabled(){
    void this.setIsLoopEnabled(!this.playbackControlState.isLoopEnabled);
  }
  async setIsLoopEnabled(isLoopEnabled: boolean, tellIframeToSetOrClearLoop = true){
    this.playbackControlState.isLoopEnabled = isLoopEnabled;
    if (isLoopEnabled){
      const {startTime, endTime} = await this.getCurrentMeasureStartAndEndTimeInSeconds();
      // const {endTime} = this.getSegmentStartAndEndTimesWithSlowRecordingIndexAccountedFor(); //if we want to loop to the end of the segment
      if(tellIframeToSetOrClearLoop){
        soundSliceSdk_setLoop(startTime, endTime);
      }
    } else if (tellIframeToSetOrClearLoop) {
      soundSliceSdk_clearLoop();
    }
    lessonPlayerStore.setIsLoopEnabled(isLoopEnabled);
  }

  async getCurrentMeasureStartAndEndTimeInSeconds(){
    if (!this.segment || !this.segment.measures){ return {startTime: 0, endTime: 0}; }
    const seekTime = await soundSlice.getCurrentTime();
    let durationTally = 0;
    for (const measure of this.segment.measures) {
      const duration = measure.durationSeconds;
      durationTally += duration;
      if (durationTally > seekTime) {
        return {startTime: durationTally - duration, endTime: durationTally};
      }
    }
    return {startTime: 0, endTime: 0};
  }

  // Prefer adding to the body of the useEffect hook from the DemoModeSelector.tsx instead of adding to this method.
  async onDemoModeOrDemoSoundEnabled_changed(): Promise<void> {
    if (this.isLoopDefined()) {
      if (!this.playbackControlState.isCountInPlaying) {
        soundSliceSdk_seek(this.loopStartOffsetSeconds);
      }
    } else {
      this.seekOffsetSeconds = await soundSlice.getCurrentTime();
      this.emitSeekOffsetAndDurationSecondsChangedEvent();
    }
  }

  async increaseSpeed() {
    await this.modifySpeed(tempoIncrementAdjustmentBPM);
  }
  async decreaseSpeed() {
    await this.modifySpeed(-tempoIncrementAdjustmentBPM);
  }

  setNoteNameVisibility(isVisible: boolean){
    soundSliceSdk_setTrackVisibility(SoundsliceTrackVisibilityTypes.PitchNames, isVisible);
    lessonPlayerStore.setAreNoteNamesVisible(isVisible);
  }
  setFingeringVisibility(isVisible: boolean){
    soundSliceSdk_setTrackVisibility(SoundsliceTrackVisibilityTypes.Fingering, isVisible);
    lessonPlayerStore.setAreStaffFingeringsVisible(isVisible);
  }

  setRhythmCountVisibility(isVisible: boolean) {
    soundSliceSdk_setTrackVisibility(SoundsliceTrackVisibilityTypes.RhythmCounts, isVisible);
    lessonPlayerStore.setDisplayRhythmCounts(isVisible);
  }
  async modifySpeed(tempoIncrementAdjustmentBPM: number){
    if (!this.segment) { return logger.lessonSettings.error({message: `segment is undefined, so no tempo to modify speed for.`}); }
    const newSpeed = (this.playbackControlState.tempo + tempoIncrementAdjustmentBPM) / this.segment.tempo;
    await this.setSpeed(newSpeed);
  }

  /**
   * Returns speed option with speed less than or equal to passed in speed.
   * e.g. for speed options [normal=1, slow=.4] would return: speed .7 Normal, .5 normal, .4 slow, .3 slow
   * @param segment
   * @param speed
   */
  getSpeedOptionWithClosestSpeed(segment: Segment, speed: number){
    const {speedOptions} = segment;
    const speedOptionWithClosestSpeed = [...speedOptions].sort((s1, s2) => Math.abs(speed - s1.value) - Math.abs(speed - s2.value))[0];
    return speedOptionWithClosestSpeed;
  }

  /**
   * Converts speed relative to normal recording index (e.g. what we send to SS), to a speed that is relative to the recording index.
   * if speed for slow recording index is .68, and speed is .68, this function will be passed .68 and return 1.
   * if speed for slow recording index is .68, and speed is .58, this function will be passed .58 and return .852941176470588
   * @param speed
   */
  getSpeedRelativeToRecordingIndex(speed: number){
    if (!this.segment) { return speed; }
    const {speedOptions} = this.segment;
    const speedOptionWithClosestSpeed = this.getSpeedOptionWithClosestSpeed(this.segment, speed);
    const hasMoreThanOneRecordingIndex = this.determineIfSegmentHasMoreThanOneRecordingIndex(speedOptions);
    let relativeSpeed = speed;
    if (hasMoreThanOneRecordingIndex && speedOptionWithClosestSpeed.label !== "Normal") { //slow should have relativeSpeed of 1 when slow backing track is used.
      const slowBackingTrackSpeedOption = speedOptions.find(s => s.label === "Slow");
      if (slowBackingTrackSpeedOption) {
        relativeSpeed = speed / slowBackingTrackSpeedOption.value;
      } else {
        logger.lessonAudio.error({message: `could not find slow backing track`});
      }
    }
    return relativeSpeed;
  }

  determineIfSegmentHasMoreThanOneRecordingIndex(speedOptions: ISpeedSelectOption[]){
    const recordingIndexes = speedOptions.map(s => s.recordingIndex);
    const uniqueRecordingIndexes = [...new Set(recordingIndexes)];
    return uniqueRecordingIndexes.length > 1;
  }

  setTempo(tempo: number){
    if (lessonService.isUserOnLessonPage() && this.segment) {
      const maxTempo = this.segment.tempo * maxSpeed;
      const minTempo = this.segment.tempo * minSpeed;
      if (tempo > maxTempo || tempo < minTempo) {
        return logger.lessonSettings.warn({message: `tempo cannot be greater than ${maxTempo} or less than ${minTempo} for this segment.`});
      }
      this.playbackControlState.tempo = tempo;
      lessonPlayerStore.setTempo(tempo);
    }
  }
}

function findNotesAtOffset(notes: Note[] | undefined, offset: number){
  const result: Note[] = [];
  if (!notes) { return result; }
  for (const note of notes) {
    if (note.offset === offset) {
      result.push(note);
    }
  }
  return result;
}

function getNotesForDemoMode(notes: Note[], demoMode: DemoMode) {
  return notes.filter(n => {
    if (demoMode === "both hands") {
      return true;
    } else if (demoMode === "right hand" && n.hand === "right hand") {
      return true;
    } else if (demoMode === "left hand" && n.hand === "left hand") {
      return true;
    }
    return false;
  });
}

const playbackOrchestrator = new PlaybackOrchestrator();
export default playbackOrchestrator;
